import {
  NIGERIAN_STATES,
  NIGERIAN_LGAS_MAP,
  NIGERIAN_AREAS,
  getStateByName,
  getLgasForState,
  isValidLgaForState,
  STATE_ALIASES,
} from '../data/nigerianLocations';

export interface ResolvedNigerianLocation {
  formattedAddress: string;
  country: string;
  state: string; // e.g. "Delta"
  lga: string; // e.g. "Oshimili South"
  cityArea: string; // e.g. "Asaba"
  streetAddress: string;
  latitude?: number;
  longitude?: number;
  placeId?: string;
  isLgaConfident: boolean;
  confidenceScore: number; // 0 to 100
  detectionMethod:
    | 'admin_level_2'
    | 'geoapify_county'
    | 'area_mapping'
    | 'address_text'
    | 'coordinate_centroid'
    | 'manual_selection';
  suggestedLgas?: string[];
  rawAddressComponents?: Array<{
    long_name: string;
    short_name: string;
    types: string[];
  }>;
}

/**
 * Normalizes state name to the official NigerianState.name in NIGERIAN_STATES.
 */
export function normalizeNigerianState(rawState: string): string {
  if (!rawState) return '';
  const clean = rawState.trim().toLowerCase();
  
  // Remove "state" suffix if present e.g. "delta state" -> "delta"
  const stripped = clean.replace(/\bstate\b/gi, '').trim();

  // Check aliases
  if (STATE_ALIASES[stripped]) {
    const canonical = STATE_ALIASES[stripped];
    const match = NIGERIAN_STATES.find(s => s.name.toLowerCase() === canonical.toLowerCase());
    if (match) return match.name;
  }

  // Exact or contains match in NIGERIAN_STATES
  const exact = NIGERIAN_STATES.find(
    s => s.name.toLowerCase() === clean || s.name.toLowerCase() === stripped
  );
  if (exact) return exact.name;

  // Fuzzy match
  const fuzzy = NIGERIAN_STATES.find(s => stripped.includes(s.name.toLowerCase()) || s.name.toLowerCase().includes(stripped));
  if (fuzzy) return fuzzy.name;

  return rawState.trim();
}

/**
 * Normalizes and matches an LGA string against the official list for the given state.
 */
export function matchLgaForState(stateName: string, candidateLga: string): string | null {
  if (!stateName || !candidateLga) return null;
  const lgas = getLgasForState(stateName);
  if (!lgas || lgas.length === 0) return null;

  const cleanCandidate = candidateLga
    .trim()
    .toLowerCase()
    .replace(/\blga\b/gi, '')
    .replace(/\blocal government area\b/gi, '')
    .trim();

  // 1. Exact match
  const exact = lgas.find(l => l.toLowerCase() === cleanCandidate);
  if (exact) return exact;

  // 2. Starts with / includes match
  const partial = lgas.find(
    l => l.toLowerCase().includes(cleanCandidate) || cleanCandidate.includes(l.toLowerCase())
  );
  if (partial) return partial;

  // 3. Normalized punctuation (slashes, hyphens)
  const normalizedCandidate = cleanCandidate.replace(/[-/]/g, ' ');
  for (const l of lgas) {
    const norm = l.toLowerCase().replace(/[-/]/g, ' ');
    if (norm === normalizedCandidate) return l;
  }

  return null;
}

/**
 * Resolves Nigerian administrative location details from address components or geocoder result.
 */
export function resolveNigerianLocationFromPlace(
  place: {
    address_components?: Array<{
      long_name: string;
      short_name: string;
      types: string[];
    }>;
    formatted_address?: string;
    geometry?: {
      location?: {
        lat: () => number;
        lng: () => number;
      } | {
        lat: number;
        lng: number;
      };
    };
    place_id?: string;
    name?: string;
  }
): ResolvedNigerianLocation {
  const components = place.address_components || [];
  const formattedAddress = place.formatted_address || place.name || '';
  let country = 'Nigeria';
  let rawState = '';
  let rawAdminLevel2 = '';
  let rawLocality = '';
  let rawSublocality = '';
  let rawNeighborhood = '';
  let rawRoute = '';
  let rawStreetNumber = '';

  for (const c of components) {
    const types = c.types;
    if (types.includes('country')) {
      country = c.long_name;
    } else if (types.includes('administrative_area_level_1')) {
      rawState = c.long_name;
    } else if (types.includes('administrative_area_level_2')) {
      rawAdminLevel2 = c.long_name;
    } else if (types.includes('locality')) {
      rawLocality = c.long_name;
    } else if (types.includes('sublocality') || types.includes('sublocality_level_1')) {
      rawSublocality = c.long_name;
    } else if (types.includes('neighborhood')) {
      rawNeighborhood = c.long_name;
    } else if (types.includes('route')) {
      rawRoute = c.long_name;
    } else if (types.includes('street_number')) {
      rawStreetNumber = c.long_name;
    }
  }

  // 1. Determine State
  let state = normalizeNigerianState(rawState);

  // If state not found in components, try to find state name in formatted address
  if (!state && formattedAddress) {
    for (const s of NIGERIAN_STATES) {
      if (new RegExp(`\\b${s.name}\\b`, 'i').test(formattedAddress)) {
        state = s.name;
        break;
      }
    }
  }

  // Default fallback if still unresolved
  if (!state) {
    state = 'Lagos';
  }

  // 2. Determine City / Area
  let cityArea = rawSublocality || rawNeighborhood || rawLocality || '';
  if (!cityArea && rawRoute) {
    cityArea = rawRoute;
  }

  // 3. Determine LGA
  let lga = '';
  let isLgaConfident = false;
  let confidenceScore = 0;
  let detectionMethod: ResolvedNigerianLocation['detectionMethod'] = 'manual_selection';

  // Strategy A: Check administrative_area_level_2 / county (commonly matches LGA in Nigeria)
  if (rawAdminLevel2) {
    const matched = matchLgaForState(state, rawAdminLevel2);
    if (matched) {
      lga = matched;
      isLgaConfident = true;
      confidenceScore = 95;
      detectionMethod = 'admin_level_2';
    }
  }

  // Strategy B: Check known Nigerian Cities/Areas mapping table in that State
  if (!isLgaConfident) {
    const stateAreas = NIGERIAN_AREAS.filter(
      a => a.stateName.toLowerCase() === state.toLowerCase()
    );

    // Test localities, sublocalities, neighborhood, name, route
    const candidates = [
      rawSublocality,
      rawNeighborhood,
      rawLocality,
      place.name,
      rawRoute,
    ].filter(Boolean) as string[];

    for (const cand of candidates) {
      const candClean = cand.toLowerCase().trim();
      const matchedArea = stateAreas.find(
        a => a.name.toLowerCase() === candClean || candClean.includes(a.name.toLowerCase())
      );
      if (matchedArea) {
        lga = matchedArea.lgaName;
        if (!cityArea) cityArea = matchedArea.name;
        isLgaConfident = true;
        confidenceScore = 90;
        detectionMethod = 'area_mapping';
        break;
      }
    }
  }

  // Strategy C: Address text matching against known areas and LGAs in that State
  if (!isLgaConfident && formattedAddress) {
    const stateLgas = getLgasForState(state);
    for (const testLga of stateLgas) {
      const regex = new RegExp(`\\b${testLga}\\b`, 'i');
      if (regex.test(formattedAddress)) {
        lga = testLga;
        isLgaConfident = true;
        confidenceScore = 85;
        detectionMethod = 'address_text';
        break;
      }
    }

    if (!isLgaConfident) {
      const stateAreas = NIGERIAN_AREAS.filter(
        a => a.stateName.toLowerCase() === state.toLowerCase()
      );
      for (const area of stateAreas) {
        const regex = new RegExp(`\\b${area.name}\\b`, 'i');
        if (regex.test(formattedAddress)) {
          lga = area.lgaName;
          if (!cityArea) cityArea = area.name;
          isLgaConfident = true;
          confidenceScore = 80;
          detectionMethod = 'area_mapping';
          break;
        }
      }
    }
  }

  // Strategy D: Specific Nigerian High-Frequency City/Area Heuristics
  if (!isLgaConfident) {
    const textToScan = `${rawLocality} ${rawSublocality} ${rawRoute} ${formattedAddress}`.toLowerCase();
    
    // Delta State key hubs: Asaba -> Oshimili South; Okpanam -> Oshimili North; Warri -> Warri South; Effurun -> Uvwie
    if (state === 'Delta') {
      if (textToScan.includes('okpanam road') || textToScan.includes('asaba') || textToScan.includes('nnebisi') || textToScan.includes('cable point')) {
        lga = 'Oshimili South';
        cityArea = cityArea || 'Asaba';
        isLgaConfident = true;
        confidenceScore = 88;
        detectionMethod = 'area_mapping';
      } else if (textToScan.includes('okpanam') && !textToScan.includes('okpanam road')) {
        lga = 'Oshimili North';
        cityArea = cityArea || 'Okpanam';
        isLgaConfident = true;
        confidenceScore = 88;
        detectionMethod = 'area_mapping';
      } else if (textToScan.includes('warri')) {
        lga = 'Warri South';
        cityArea = cityArea || 'Warri';
        isLgaConfident = true;
        confidenceScore = 85;
        detectionMethod = 'area_mapping';
      } else if (textToScan.includes('effurun')) {
        lga = 'Uvwie';
        cityArea = cityArea || 'Effurun';
        isLgaConfident = true;
        confidenceScore = 90;
        detectionMethod = 'area_mapping';
      }
    } else if (state === 'Rivers') {
      if (textToScan.includes('rumuola') || textToScan.includes('rumuokoro') || textToScan.includes('trans-amadi') || textToScan.includes('woji') || textToScan.includes('choba')) {
        lga = 'Obio/Akpor';
        isLgaConfident = true;
        confidenceScore = 90;
        detectionMethod = 'area_mapping';
      } else if (textToScan.includes('port harcourt') || textToScan.includes('diobu') || textToScan.includes('d-line') || textToScan.includes('borokiri')) {
        lga = 'Port Harcourt';
        isLgaConfident = true;
        confidenceScore = 85;
        detectionMethod = 'area_mapping';
      }
    } else if (state === 'Lagos') {
      if (textToScan.includes('ikeja') || textToScan.includes('alausa') || textToScan.includes('allen') || textToScan.includes('opebi')) {
        lga = 'Ikeja';
        isLgaConfident = true;
        confidenceScore = 95;
        detectionMethod = 'area_mapping';
      } else if (textToScan.includes('victoria island') || textToScan.includes('lekki') || textToScan.includes('ikoyi') || textToScan.includes('ajah')) {
        lga = 'Eti-Osa';
        isLgaConfident = true;
        confidenceScore = 90;
        detectionMethod = 'area_mapping';
      } else if (textToScan.includes('yaba') || textToScan.includes('ebute metta') || textToScan.includes('akoka')) {
        lga = 'Lagos Mainland';
        isLgaConfident = true;
        confidenceScore = 90;
        detectionMethod = 'area_mapping';
      } else if (textToScan.includes('surulere') || textToScan.includes('bode thomas') || textToScan.includes('ojuelegba')) {
        lga = 'Surulere';
        isLgaConfident = true;
        confidenceScore = 90;
        detectionMethod = 'area_mapping';
      }
    } else if (state === 'Federal Capital Territory (FCT)') {
      if (textToScan.includes('wuse') || textToScan.includes('maitama') || textToScan.includes('garki') || textToScan.includes('asokoro') || textToScan.includes('gwarinpa') || textToScan.includes('central business') || textToScan.includes('apo')) {
        lga = 'Abuja Municipal Area Council';
        isLgaConfident = true;
        confidenceScore = 95;
        detectionMethod = 'area_mapping';
      } else if (textToScan.includes('kubwa') || textToScan.includes('dutse') || textToScan.includes('bwari')) {
        lga = 'Bwari';
        isLgaConfident = true;
        confidenceScore = 90;
        detectionMethod = 'area_mapping';
      }
    }
  }

  // Fallback LGA if not detected: pick capital LGA or first LGA for that state, but mark as NOT confident
  const validLgas = getLgasForState(state);
  if (!lga || !isValidLgaForState(state, lga)) {
    lga = validLgas[0] || 'Ikeja';
    isLgaConfident = false;
    confidenceScore = 30;
    detectionMethod = 'manual_selection';
  }

  // Extract coordinates
  let latitude: number | undefined;
  let longitude: number | undefined;
  if (place.geometry?.location) {
    const loc = place.geometry.location as any;
    if (typeof loc?.lat === 'function') {
      latitude = loc.lat();
      longitude = loc.lng();
    } else if (loc) {
      latitude = typeof loc.lat === 'number' ? loc.lat : parseFloat(loc.lat);
      longitude = typeof loc.lng === 'number' ? loc.lng : parseFloat(loc.lng);
    }
  }

  // Format clean street address
  let streetAddress = '';
  if (rawStreetNumber && rawRoute) {
    streetAddress = `${rawStreetNumber} ${rawRoute}`;
  } else if (rawRoute) {
    streetAddress = rawRoute;
  } else if (place.name && place.name !== rawLocality && place.name !== state) {
    streetAddress = place.name;
  } else {
    streetAddress = formattedAddress.split(',')[0] || '';
  }

  return {
    formattedAddress,
    country,
    state,
    lga,
    cityArea: cityArea || lga,
    streetAddress,
    latitude,
    longitude,
    placeId: place.place_id,
    isLgaConfident,
    confidenceScore,
    detectionMethod,
    suggestedLgas: validLgas,
    rawAddressComponents: components,
  };
}

/**
 * Centroid coordinates for major Nigerian states & capitals
 * Used for distance proximity calculations when exact GPS coordinates are omitted.
 */
export const NIGERIAN_LOCATION_COORDINATES: Record<string, { lat: number; lng: number }> = {
  'Abia': { lat: 5.4309, lng: 7.5247 }, // Umuahia
  'Adamawa': { lat: 9.3265, lng: 12.4379 }, // Yola
  'Akwa Ibom': { lat: 5.0377, lng: 7.9128 }, // Uyo
  'Anambra': { lat: 6.2209, lng: 7.0723 }, // Awka
  'Bauchi': { lat: 10.3158, lng: 9.8442 }, // Bauchi
  'Bayelsa': { lat: 4.9267, lng: 6.2676 }, // Yenagoa
  'Benue': { lat: 7.7322, lng: 8.5214 }, // Makurdi
  'Borno': { lat: 11.8311, lng: 13.151 }, // Maiduguri
  'Cross River': { lat: 4.9757, lng: 8.3417 }, // Calabar
  'Delta': { lat: 6.1983, lng: 6.7328 }, // Asaba
  'Ebonyi': { lat: 6.3249, lng: 8.1137 }, // Abakaliki
  'Edo': { lat: 6.335, lng: 5.6037 }, // Benin City
  'Ekiti': { lat: 7.6219, lng: 5.2215 }, // Ado Ekiti
  'Enugu': { lat: 6.4584, lng: 7.5464 }, // Enugu
  'Federal Capital Territory (FCT)': { lat: 9.0765, lng: 7.3986 }, // Abuja
  'Gombe': { lat: 10.2897, lng: 11.1673 }, // Gombe
  'Imo': { lat: 5.4852, lng: 7.0355 }, // Owerri
  'Jigawa': { lat: 11.7583, lng: 9.3392 }, // Dutse
  'Kaduna': { lat: 10.5105, lng: 7.4165 }, // Kaduna
  'Kano': { lat: 12.0022, lng: 8.592 }, // Kano
  'Katsina': { lat: 12.9908, lng: 7.6018 }, // Katsina
  'Kebbi': { lat: 12.4504, lng: 4.1999 }, // Birnin Kebbi
  'Kogi': { lat: 7.7969, lng: 6.7405 }, // Lokoja
  'Kwara': { lat: 8.4799, lng: 4.5418 }, // Ilorin
  'Lagos': { lat: 6.5244, lng: 3.3792 }, // Lagos
  'Nasarawa': { lat: 8.4932, lng: 8.5153 }, // Lafia
  'Niger': { lat: 9.6143, lng: 6.5569 }, // Minna
  'Ogun': { lat: 7.1475, lng: 3.3619 }, // Abeokuta
  'Ondo': { lat: 7.2571, lng: 5.2058 }, // Akure
  'Osun': { lat: 7.7827, lng: 4.5418 }, // Osogbo
  'Oyo': { lat: 7.3775, lng: 3.947 }, // Ibadan
  'Plateau': { lat: 9.8965, lng: 8.8583 }, // Jos
  'Rivers': { lat: 4.8156, lng: 7.0498 }, // Port Harcourt
  'Sokoto': { lat: 13.0059, lng: 5.2476 }, // Sokoto
  'Taraba': { lat: 8.8937, lng: 11.3601 }, // Jalingo
  'Yobe': { lat: 11.747, lng: 11.9608 }, // Damaturu
  'Zamfara': { lat: 12.1628, lng: 6.6614 }, // Gusau
  // Key hubs & Major LGAs across Nigeria
  // Lagos State LGAs & Hubs
  'Ikeja': { lat: 6.5965, lng: 3.3421 },
  'Alimosho': { lat: 6.6111, lng: 3.2572 },
  'Kosofe': { lat: 6.5828, lng: 3.3912 },
  'Oshodi-Isolo': { lat: 6.5333, lng: 3.3167 },
  'Oshodi': { lat: 6.5333, lng: 3.3167 },
  'Isolo': { lat: 6.5200, lng: 3.3250 },
  'Surulere': { lat: 6.4969, lng: 3.3512 },
  'Agege': { lat: 6.6180, lng: 3.3209 },
  'Ifako-Ijaiye': { lat: 6.6710, lng: 3.3100 },
  'Ikorodu': { lat: 6.6194, lng: 3.5105 },
  'Eti-Osa': { lat: 6.4474, lng: 3.5356 },
  'Eti Osa': { lat: 6.4474, lng: 3.5356 },
  'Victoria Island': { lat: 6.4281, lng: 3.4219 },
  'Lekki': { lat: 6.4698, lng: 3.5852 },
  'Ikoyi': { lat: 6.4549, lng: 3.4357 },
  'Lagos Island': { lat: 6.4549, lng: 3.4046 },
  'Lagos Mainland': { lat: 6.4947, lng: 3.3768 },
  'Yaba': { lat: 6.5095, lng: 3.3711 },
  'Amuwo-Odofin': { lat: 6.4520, lng: 3.2917 },
  'Festac': { lat: 6.4667, lng: 3.2833 },
  'Ojo': { lat: 6.4600, lng: 3.1700 },
  'Apapa': { lat: 6.4481, lng: 3.3644 },
  'Somolu': { lat: 6.5375, lng: 3.3750 },
  'Shomolu': { lat: 6.5375, lng: 3.3750 },
  'Mushin': { lat: 6.5300, lng: 3.3500 },
  'Ajeromi-Ifelodun': { lat: 6.4600, lng: 3.3400 },
  'Ajegunle': { lat: 6.4500, lng: 3.3333 },
  'Ibeju-Lekki': { lat: 6.4900, lng: 3.8200 },
  'Epe': { lat: 6.5841, lng: 3.9834 },
  'Badagry': { lat: 6.4316, lng: 2.8876 },

  // FCT / Abuja Municipal & Satellite Hubs
  'Abuja Municipal': { lat: 9.0765, lng: 7.3986 },
  'Bwari': { lat: 9.2889, lng: 7.3789 },
  'Gwagwalada': { lat: 8.9431, lng: 7.0864 },
  'Kuje': { lat: 8.8797, lng: 7.2275 },
  'Kwali': { lat: 8.7167, lng: 7.0167 },
  'Maitama': { lat: 9.0882, lng: 7.4933 },
  'Wuse': { lat: 9.0667, lng: 7.4667 },
  'Garki': { lat: 9.0333, lng: 7.4833 },
  'Jabi': { lat: 9.0667, lng: 7.4167 },
  'Gwarinpa': { lat: 9.1167, lng: 7.3833 },

  // Rivers State LGAs
  'Port Harcourt': { lat: 4.8156, lng: 7.0498 },
  'Obio-Akpor': { lat: 4.8500, lng: 6.9833 },
  'Eleme': { lat: 4.7900, lng: 7.1200 },
  'Ikwerre': { lat: 4.9833, lng: 6.9167 },
  'Oyigbo': { lat: 4.8833, lng: 7.1500 },

  // Oyo State LGAs
  'Ibadan North': { lat: 7.4167, lng: 3.9000 },
  'Ibadan North-East': { lat: 7.3833, lng: 3.9333 },
  'Ibadan South-West': { lat: 7.3667, lng: 3.8667 },
  'Ibadan South-East': { lat: 7.3500, lng: 3.9167 },
  'Ibadan North-West': { lat: 7.4000, lng: 3.8833 },
  'Oluyole': { lat: 7.3167, lng: 3.8667 },
  'Egbeda': { lat: 7.3833, lng: 4.0500 },
  'Akinyele': { lat: 7.5167, lng: 3.9167 },

  // Ogun State LGAs
  'Abeokuta South': { lat: 7.1475, lng: 3.3619 },
  'Abeokuta North': { lat: 7.2000, lng: 3.3167 },
  'Ado-Odo/Ota': { lat: 6.6833, lng: 3.1667 },
  'Ota': { lat: 6.6833, lng: 3.1667 },
  'Ifo': { lat: 6.8167, lng: 3.2000 },
  'Sagamu': { lat: 6.8400, lng: 3.6500 },
  'Ijebu Ode': { lat: 6.8200, lng: 3.9200 },

  // Kano State LGAs
  'Kano Municipal': { lat: 12.0022, lng: 8.5920 },
  'Fagge': { lat: 12.0167, lng: 8.5333 },
  'Dala': { lat: 12.0000, lng: 8.5000 },
  'Gwale': { lat: 11.9833, lng: 8.5167 },
  'Nasarawa (Kano)': { lat: 12.0000, lng: 8.5500 },
  'Tarauni': { lat: 11.9667, lng: 8.5500 },

  // Delta State LGAs & Hubs
  'Oshimili South': { lat: 6.1983, lng: 6.7328 },
  'Oshimili North': { lat: 6.2667, lng: 6.6500 },
  'Warri South': { lat: 5.5175, lng: 5.7501 },
  'Warri North': { lat: 5.8000, lng: 5.4833 },
  'Uvwie': { lat: 5.5569, lng: 5.7828 },
  'Asaba': { lat: 6.1983, lng: 6.7328 },
  'Warri': { lat: 5.5175, lng: 5.7501 },
  'Effurun': { lat: 5.5569, lng: 5.7828 },
  'Sapele': { lat: 5.8942, lng: 5.6767 },
  'Ughelli': { lat: 5.5000, lng: 6.0000 },

  // Edo State LGAs & Hubs
  'Oredo': { lat: 6.3350, lng: 5.6037 },
  'Ikpoba Okha': { lat: 6.3000, lng: 5.6500 },
  'Egor': { lat: 6.3667, lng: 5.5833 },
  'Benin City': { lat: 6.3350, lng: 5.6037 },

  // Abia State Hubs
  'Aba': { lat: 5.1066, lng: 7.3667 },
  'Aba North': { lat: 5.1200, lng: 7.3700 },
  'Aba South': { lat: 5.1000, lng: 7.3600 },
};

/**
 * Returns estimated coordinates for distance calculations.
 * Hierarchical resolution:
 * 1. Specific neighborhood / cityArea (if present in coordinates map)
 * 2. Specific LGA (if present in coordinates map)
 * 3. State centroid fallback
 */
export function getEstimatedCoordinates(
  stateName?: string,
  lgaName?: string,
  cityArea?: string
): { lat: number; lng: number } | null {
  // Helper to match key case-insensitively
  const findCoord = (query?: string): { lat: number; lng: number } | null => {
    if (!query) return null;
    const clean = query.trim().toLowerCase();
    for (const [key, val] of Object.entries(NIGERIAN_LOCATION_COORDINATES)) {
      if (key.toLowerCase() === clean) return val;
    }
    return null;
  };

  // 1. Check cityArea
  if (cityArea) {
    const areaCoord = findCoord(cityArea);
    if (areaCoord) return areaCoord;
  }

  // 2. Check LGA
  if (lgaName) {
    const lgaCoord = findCoord(lgaName);
    if (lgaCoord) return lgaCoord;
  }

  // 3. Fallback to state centroid
  if (stateName) {
    const norm = normalizeNigerianState(stateName);
    const stateCoord = findCoord(norm);
    if (stateCoord) return stateCoord;
  }

  return null;
}

/**
 * Standard Haversine distance formula between two GPS coordinates in kilometers
 */
export function calculateHaversineDistanceKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371; // Earth's mean radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

